import api from '@/api';
import Team from '@/services/Team/Team';
import dayjs from 'dayjs';
import { JOBS_VIEW_JOB_REQUESTS, PROPERTIES_VIEW_PROPERTIES } from '@/config/permission';

class User {
  /**
   * @param credentials
   * @returns {AxiosPromise<any>}
   */
  static login(credentials) {
    return api.post('/auth/login', credentials);
  }

  /**
   * @returns {AxiosPromise<any>}
   */
  static refresh() {
    return api.post('/auth/refresh');
  }

  /**
   * @returns {AxiosPromise<any>}
   */
  static list(params = {}) {
    return api.get('/users', { params });
  }

  /**
   * @returns {AxiosPromise<any>}
   */
  static searchByEmail(email) {
    return api.get('/users/email', { params: { email } });
  }

  /**
   * @returns {AxiosPromise<any>}
   */
  static find(userId) {
    return api.get(`/users/${userId}`);
  }

  /**
   * Returns teams that the user can show in a dropdown based on their role
   * Also returns formatted.
   * @returns {*}
   */
  static getAvailableTeams() {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await api.get('/teams/available');
        const teams = data.data;
        resolve(Team.formatTeams(teams));
      } catch (e) {
        reject(e);
      }
    });
  }

  static getManagedTeams() {
    return api.get('/users/teams', { params: { managed: true } });
  }

  /**
   * @returns {AxiosPromise<any>}
   */
  static update(userId, params) {
    return api.put(`/users/${userId}`, params);
  }

  /**
   * @returns {AxiosPromise<any>}
   */
  static me() {
    return api.get('/auth/me');
  }

  /**
   * @returns {AxiosPromise<any>}
   */
  static logout() {
    return api.post('/auth/logout');
  }

  /**
   * @returns {AxiosPromise<any>}
   */
  static store(params) {
    return api.post('/users', params);
  }

  /**
   * Sends forgot password email
   * @param email
   * @returns {AxiosPromise<any>}
   */
  static forgotPassword(email) {
    return api.post('/auth/forgot', { email });
  }

  /**
   * Accept terms and conditions for current user
   * @returns {AxiosPromise<any>}
   */
  static acceptTerms() {
    return api.post('/auth/terms', { accepted: 1 });
  }

  /**
   * Gets the terms and conditions
   * @returns {AxiosPromise<any>}
   */
  static getEULA() {
    return api.get('/auth/terms');
  }

  /**
   * Resets the users password
   * @param params
   * Requires: {
   *   token,
   *   password,
   *   password_confirmation
   * }
   * @returns {AxiosPromise<any>}
   */
  static resetPassword(params) {
    return api.post('/auth/reset', params);
  }

  /**
   * If the time is under 10 min, refresh
   * @param date
   * @returns {boolean}
   */
  static needsTokenRefresh(date) {
    if (!date) {
      return true;
    }

    const now = dayjs();

    const diff = dayjs(date).diff(now, 'minutes');

    return diff < 10;
  }
  /**
   * Has the refresh token expired?
   * @param date
   * @returns {boolean}
   */
  static hasExpiredToken(date) {
    if (!date) {
      return false;
    }

    return dayjs().isAfter(dayjs(date));
  }

  /**
   * Change a client ID for a user
   * @param clientId
   * @returns {AxiosPromise<any>}
   */
  static changeClient(clientId) {
    const params = {
      clientId,
    };

    return api.put('/users/client', params);
  }

  /**
   *
   * @param code
   * @param state
   * @returns {Promise<AxiosResponse<T>>}
   */
  static getToken(code, state = null) {
    return api.get('/auth/token', {
      params: {
        code,
        state
      }
    });
  }

  /*
   * Get file download info
   * @param clientUserDownloadId
   */
  static getDownloadInfo(clientUserDownloadId) {

    const params = {
      clientUserDownloadId,
    };

    return api.get('/users/download', { params: params });
  }

  /**
   * Download a file
   * @param clientUserDownloadId
   */
  static download(clientUserDownloadId, filename) {

    const params = {
      clientUserDownloadId,
    };

    return api.post('/users/download', params, {
      responseType: 'arraybuffer',
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
    });
  }

  static getDefaultRouteForPermission(permissions) {
    if (permissions.includes(JOBS_VIEW_JOB_REQUESTS)) {
      return 'property-dashboard';
    }

    return 'actions-dashboard';
  }
}

export default User;
